/**
 * Application Constants
 * Centralized configuration for API URLs, scenarios, etc.
 */

/**
 * API Configuration
 */
export const API_CONFIG = {
  // In production (served from same origin), use relative URLs
  // In development, use localhost
  BASE_URL: import.meta.env.VITE_BACKEND_API_URL ?? 'http://localhost:8001',
  TIMEOUT: 30_000, // 30 seconds
  STREAM_TIMEOUT: 300_000, // 5 minutes for streaming
} as const

/**
 * Available scenarios for chat queries
 */
export const SCENARIOS = [
  'default',
  'impact_analysis',
  'requirements_validation',
  'code_documentation',
  // 'template_generation', // Hidden temporarily
  'mermaid_generation',
] as const

export type Scenario = (typeof SCENARIOS)[number]

/**
 * Available search modes
 */
export const SEARCH_MODES = ['smart', 'direct', 'agent'] as const

export type SearchMode = (typeof SEARCH_MODES)[number]

/**
 * File upload configuration
 */
export const FILE_UPLOAD = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB
  ACCEPTED_TYPES: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'text/plain',
    'text/markdown',
    'image/png',
    'image/jpeg',
    'image/webp',
    'application/json',
    'text/x-python',
    'text/javascript',
    'text/typescript',
  ],
  ACCEPTED_EXTENSIONS: ['.pdf', '.docx', '.txt', '.md', '.png', '.jpg', '.jpeg', '.webp'],
} as const

/**
 * Chat configuration
 */
export const CHAT_CONFIG = {
  MAX_MESSAGE_LENGTH: 4000,
  AUTO_SCROLL_THRESHOLD: 100, // pixels from bottom
  CHUNK_UPDATE_THROTTLE: 50, // ms between UI updates
} as const

/**
 * Local storage keys
 */
export const STORAGE_KEYS = {
  THEME: 'app-theme',
  LANGUAGE: 'app-language',
  LAST_PROJECT: 'last-project-id',
  SIDEBAR_COLLAPSED: 'sidebar-collapsed',
} as const

/**
 * Query keys for TanStack Query
 */
export const QUERY_KEYS = {
  PROJECTS: ['projects'] as const,
  PROJECT: (id: string) => ['projects', id] as const,
  TEMPLATES: (projectId: string) => ['templates', projectId] as const,
  TEMPLATE_CONTENT: (projectId: string, templateName: string) =>
    ['templates', projectId, templateName, 'content'] as const,
  PROJECT_TAGS: (projectId: string) => ['projects', projectId, 'tags'] as const,
  HEALTH: ['health'] as const,
} as const
